// Ores (UX §6.3, research §2): seeded jittered rocks with material flecks.
// Pebble → twin-lobe Chunk → tri-lobe Lump → Boulder with a crystal crown.
import type { Part } from '../passes.ts';
import { blobPts } from '../shapes.ts';
import type { ItemKit } from './kit.ts';

interface OreStyle {
  body: string;
  fleck: string;
  /** coal: angular glint facets instead of round flecks. */
  facets: boolean;
  /** Outline: points, radius jitter, corner rounding (0 = smooth curve) — a silhouette per material. */
  pts: number;
  jit: number;
  corner: number;
}

const ORE: Record<string, OreStyle> = {
  // lead: a smooth, heavy pebble
  ore_lead: {
    body: 'material.lead',
    fleck: 'material.steel',
    facets: false,
    pts: 9,
    jit: 0.07,
    corner: 0,
  },
  // coal: angular, faceted
  ore_coal: {
    body: 'material.coal',
    fleck: 'material.coalGlint',
    facets: true,
    pts: 7,
    jit: 0.12,
    corner: 0.03,
  },
  // copper: knobbly nugget
  ore_copper: {
    body: 'material.copper',
    fleck: 'material.copperPatina',
    facets: false,
    pts: 13,
    jit: 0.16,
    corner: 0,
  },
  // iron: blocky, chipped
  ore_iron: {
    body: 'material.iron',
    fleck: 'material.steel',
    facets: false,
    pts: 6,
    jit: 0.1,
    corner: 0.06,
  },
};

type Lobe = readonly [number, number, number, number];

/** Lobes (box-relative centre and radii) by tier. */
const LOBES: readonly (readonly Lobe[])[] = [
  [[0.5, 0.56, 0.48, 0.38]],
  [
    [0.34, 0.6, 0.33, 0.31],
    [0.64, 0.48, 0.35, 0.36],
  ],
  [
    [0.5, 0.33, 0.33, 0.28],
    [0.28, 0.64, 0.28, 0.28],
    [0.71, 0.63, 0.29, 0.29],
  ],
  [[0.5, 0.62, 0.5, 0.37]],
];

/** Boulder crown: centre x, tip y, half width, tilt (degrees). */
const CROWN: readonly (readonly [number, number, number, number])[] = [
  [0.3, 0.3, 0.1, -14],
  [0.52, 0.16, 0.13, 4],
  [0.73, 0.3, 0.1, 18],
];

export function oreParts(k: ItemKit, fam: string): Part[] {
  const st = ORE[fam] ?? (ORE.ore_lead as OreStyle);
  const fl = k.sw(st.fleck);
  const parts: Part[] = [];
  if (k.tier >= 4) {
    for (const [cx, top, hw, tilt] of CROWN) {
      const sx = Math.sin((tilt * Math.PI) / 180);
      const tip: [number, number] = [cx + sx * 0.36, top - 0.04];
      parts.push(
        k.poly(
          st.fleck,
          [
            [cx - hw, 0.5],
            [cx - hw + sx * 0.3, top + 0.08],
            tip,
            [cx + hw + sx * 0.3, top + 0.08],
            [cx + hw, 0.5],
          ],
          0.02,
          { detail: () => k.stroke([tip, [cx + sx * 0.12, 0.46]], fl.light, k.ow * 0.5) },
        ),
      );
    }
  }
  const lobes = LOBES[Math.min(4, k.tier) - 1] ?? [];
  for (const [cx, cy, rx, ry] of lobes) {
    const pts = blobPts(k.rng, k.X(cx), k.Y(cy), k.S(rx), k.S(ry), st.pts, st.jit, k.rng() * 6);
    const flecks: [number, number, number][] = [];
    const n = k.tier >= 4 ? 7 : 4;
    for (let i = 0; i < n; i++) {
      const a = k.rng() * Math.PI * 2;
      const d = Math.sqrt(k.rng()) * 0.6;
      flecks.push([
        cx + Math.cos(a) * rx * d,
        cy + Math.sin(a) * ry * d * 0.9,
        0.035 + k.rng() * 0.04,
      ]);
    }
    const shape = (extra: Partial<Part>): Part =>
      st.corner > 0
        ? k.polyAbs(st.body, pts, k.S(st.corner), extra)
        : k.smooth(st.body, pts, extra);
    parts.push(
      shape({
        detail: () => {
          for (const [fx, fy, r] of flecks) {
            if (st.facets) {
              k.c.beginPath();
              k.c.moveTo(k.X(fx - r * 1.3), k.Y(fy + r));
              k.c.lineTo(k.X(fx), k.Y(fy - r * 1.4));
              k.c.lineTo(k.X(fx + r * 1.2), k.Y(fy + r * 0.8));
              k.c.closePath();
              k.c.fillStyle = fl.base;
              k.c.fill();
              k.c.beginPath();
              k.c.moveTo(k.X(fx), k.Y(fy - r * 1.4));
              k.c.lineTo(k.X(fx + r * 1.2), k.Y(fy + r * 0.8));
              k.c.lineTo(k.X(fx + r * 0.2), k.Y(fy + r * 0.4));
              k.c.closePath();
              k.c.fillStyle = fl.light;
              k.c.fill();
            } else {
              k.fillEll(fx, fy, r * 1.25, r, fl.base);
              k.fillEll(fx - r * 0.3, fy - r * 0.3, r * 0.45, r * 0.35, fl.hi);
            }
          }
        },
      }),
    );
  }
  return parts;
}
